import { ENV } from '@/constants';
import { exploreSchemaItemList } from '../exploreSchema';

export const schemaObject_TextToArtAi = {
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'WebPage',
			'@id': 'https://liftapp.ai/text-to-art-ai',
			name: 'Text to Art AI Generator — Create AI Artwork Online | Lift',
			description:
				'Turn text to AI art with Lift instantly. No watermark. Enter your prompt, pick a style and ratio, and generate unique AI artwork from text in seconds — no design skills needed. Try now!',
			url: 'https://liftapp.ai/text-to-art-ai',
			image: 'https://liftapp.ai/seo-image.webp',
			mainEntity: [
				{ '@id': 'https://liftapp.ai/text-to-art-ai#product' },
				{ '@id': 'https://liftapp.ai/text-to-art-ai#howto' },
				{ '@id': 'https://liftapp.ai/text-to-art-ai#benefits' },
				{ '@id': 'https://liftapp.ai/text-to-art-ai#faq' },
				{ '@id': 'https://liftapp.ai/text-to-art-ai#why-lift' },
				{ '@id': 'https://liftapp.ai/text-to-art-ai#explore-tools' },
				{ '@id': 'https://liftapp.ai/text-to-art-ai#all-in-one' },
			],
		},
		// product
		{
			'@type': 'Product',
			'@id': 'https://liftapp.ai/text-to-art-ai#product',
			name: 'AI Text to Art Generator',
			description:
				'Turn text to AI art with Lift instantly. No watermark. Enter your prompt, pick a style and ratio, and generate unique AI artwork from text in seconds.',
			url: 'https://liftapp.ai/text-to-art-ai',
			fileFormat: ['image/jpeg', 'image/png', 'image/webp'],
			memoryRequirements: 'Max file size: 20MB',
			brand: {
				'@type': 'Brand',
				name: 'Lift',
			},
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: '4.8',
				reviewCount: 130000,
				bestRating: '5',
				worstRating: '1',
			},
			interactionStatistic: [
				{
					'@type': 'InteractionCounter',
					interactionType: { '@type': 'InstallAction' },
					userInteractionCount: 13000000,
				},
			],
		},
		// howto
		{
			'@type': 'HowTo',
			'@id': 'https://liftapp.ai/text-to-art-ai#howto',
			name: 'How it works',
			step: [
				{
					'@type': 'HowToStep',
					position: 1,
					name: 'Enter your prompt or pick a template',
					text: 'Type what you want to create or choose from ready-made templates to get started instantly',
				},
				{
					'@type': 'HowToStep',
					position: 2,
					name: 'Choose style, ratio, and model',
					text: 'Select a visual style, set your preferred aspect ratio, and pick an AI model — or optionally upload a reference image to guide the generation',
				},
				{
					'@type': 'HowToStep',
					position: 3,
					name: 'Let the AI do the work',
					text: 'Watch as Lift transforms your text prompt into a unique, high-quality AI-generated image automatically',
				},
				{
					'@type': 'HowToStep',
					position: 4,
					name: 'Download and use',
					text: 'Download your image or publish it straight to your social feeds',
				},
			],
		},
		// benefit
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/text-to-art-ai#benefits',
			name: 'Who can benefit from AI Text to Art Generator?',
			description:
				"Content creators, marketers, designers, small business owners, and artists can benefit from Lift's AI Text to Art Generator for creating unique visuals from text.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Content Creators — Generate scroll-stopping visuals for Instagram, TikTok, and YouTube in seconds — no photoshoots needed.',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Marketers & SMM Managers — Produce high-volume ad creatives, banners, and promo visuals by simply adjusting prompts, styles, and ratios.',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Designers & Freelancers — Use AI text to art generation to rapidly explore concepts and draft visual directions.',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Small Business Owners — Create professional product mockups, branded promo images, and marketing visuals instantly.',
				},
				{
					'@type': 'ListItem',
					position: 5,
					name: 'Artists & AI Art Enthusiasts — Experiment freely with styles, prompts, cinematic presets, and model selection.',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/benefits.3da6de53.webp',
				name: 'AI text to art generator',
				description:
					'Example of how Lift AI Text to Art Generator creates unique artwork from text prompts.',
			},
		},

		// faq
		{
			'@type': 'FAQPage',
			'@id': 'https://liftapp.ai/text-to-art-ai#faq',
			mainEntity: [
				{
					'@type': 'Question',
					name: 'What is Lift AI Text to Art Generator?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Lift AI Text to Art Generator is a premium online tool that instantly converts any text prompt into a unique, high-quality image — ideal for content creators, marketers, designers, and anyone who needs powerful visuals without complex software.',
					},
				},
				{
					'@type': 'Question',
					name: 'How does Lift AI Text to Art Generator work?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Simply enter your prompt or pick a template in Lift's text to art AI tool. Choose your preferred style, ratio, and model — or upload a reference image for guided generation. Hit Generate and get your AI-created image in seconds — no design experience needed.",
					},
				},
				{
					'@type': 'Question',
					name: 'Can I customize the style and format of generated images?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Yes. Lift gives you full control over style presets, aspect ratio, and AI model selection. You can also upload a reference image to steer the visual direction, then Remix or Regenerate to fine-tune the result until it's exactly what you envisioned.",
					},
				},
				{
					'@type': 'Question',
					name: 'Can I download the results without a watermark?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Absolutely. All images generated or edited in Lift can be exported without watermark when you're on the Pro plan. Download them in high-res PNG or JPG — ready for social media, print, ads, or e-commerce.",
					},
				},
			],
		},

		// why lift
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/text-to-art-ai#why-lift',
			name: "Why Lift's AI Text to Art Generator stands out",
			description:
				"Lift's AI Text to Art Generator combines professional-grade AI, ease of use, and affordability — making it a trusted choice for millions of users worldwide.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'High-quality results',
					description:
						'AI-powered technology delivers professional-grade visuals every time',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Intelligent rendering',
					description:
						'AI tailors the effect to the scene and subject for optimal results',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Cross-platform support',
					description: 'Available on iOS, Android, and Web for seamless access',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Budget-friendly',
					description:
						'Save on costly designers with an affordable and intuitive tool',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/whyUse.ae83a89c.webp',
				name: 'Prompt to Art AI generator',
				description:
					"Comparison of Lift's AI Text to Art Generator benefits — high quality, intelligent rendering, multi-platform access, and cost savings.",
			},
		},
		// explore
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/text-to-art-ai#explore-tools',
			name: 'Explore the AI tools',
			description:
				'A curated list of AI-powered tools for image generation, enhancement, background removal, and more – all available on Lift.',
			itemListOrder: 'Unordered',
			...exploreSchemaItemList,
		},

		{
			'@type': 'SoftwareApplication',
			'@id': 'https://liftapp.ai/text-to-art-ai#all-in-one',
			name: 'Try Lift – AI Content Creation Tools',
			description:
				'Your all-in-one tools to create, edit, and elevate your content. Create stunning content with AI tools, templates, and editing features effortlessly.',
			operatingSystem: 'iOS, Android, Web',
			applicationCategory: 'MultimediaApplication',
			url: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
			applicationSubCategory: 'Content Creation',
			publisher: {
				'@type': 'Organization',
				name: 'Lift',
				url: 'https://liftapp.ai',
			},
			potentialAction: [
				{
					'@type': 'ViewAction',
					name: 'Open Web App',
					target: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
				},
			],
		},

		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://liftapp.ai',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'AI Text to Art Generator',
					item: 'https://liftapp.ai/text-to-art-ai',
				},
			],
		},
	],
};
